import { useMemo } from 'react';

import type { ApolloLink, WatchQueryFetchPolicy } from '@apollo/client';
import { gql } from '@apollo/client';
import { uniqWith } from 'ramda';
import { useCurrentUserQuery } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { getIsBasicUser, getIsFullUser, orgTypename } from 'src/helpers';
import { useGetOrgMembers } from 'src/hooks/user';

export function useAddOrgMember(): {
  addOrgMember: (
    orgId: UUID,
    options: Parameters<Apollo.AddOrgMemberMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.AddOrgMemberMutation>>;
} {
  const [addOrgMember] = Apollo.useAddOrgMemberMutation();

  return {
    addOrgMember: (orgId, options) => {
      return addOrgMember({
        ...options,
        update: (cache, { data }) => {
          cache.modify({
            fields: {
              members: (existingMemberRefs, { readField }) => {
                const newMemberRef = cache.writeFragment({
                  data: data?.addOrgMember.user,
                  fragment: gql`
                    fragment NewMember on User {
                      __typename
                      id
                    }
                  `,
                });

                return uniqWith(
                  (a, b) => readField<UUID>('id', a) === readField<UUID>('id', b),
                  [...existingMemberRefs, newMemberRef]
                );
              },
            },
            id: cache.identify({ __typename: orgTypename, id: orgId }),
          });
        },
      });
    },
  };
}

export function useGetOrgMembersBySeatType(options?: {
  fetchPolicy?: WatchQueryFetchPolicy;
  nextFetchPolicy?: WatchQueryFetchPolicy;
}) {
  const { currentUser, loading: loadingUser } = useCurrentUserQuery(options);
  const { loading: loadingOrgMembers, orgMembers } = useGetOrgMembers({
    variables: { filter: Apollo.OrgMemberFilter.Active },
    ...options,
  });
  const fullSeatOrgMembers = useMemo(() => orgMembers.filter(getIsFullUser), [orgMembers]);
  const basicSeatOrgMembers = useMemo(() => orgMembers.filter(getIsBasicUser), [orgMembers]);

  return {
    basicSeatOrgMembers,
    currentUser,
    fullSeatOrgMembers,
    loading: loadingUser || loadingOrgMembers,
    numBasicSeatsPurchased: currentUser?.org.alineSubscription?.numBasicSeats ?? 0,
    numFullSeatsPurchased: currentUser?.org.alineSubscription?.numFullSeats ?? 0,
  };
}
